import { useState } from 'react'
import { DsIsland } from '@/design-system/island'
import { Button, Dialog, Field, Input } from '@/design-system/primitives'

interface CreateTeamModalProps {
  isOpen: boolean
  onClose: () => void
  onCreate: (organizationName?: string) => Promise<void>
  isCreating: boolean
}

/**
 * Starting a second workspace.
 *
 * It was a hand-rolled overlay with an icon plate beside the title and a form
 * whose input had its focus ring switched off. The system's dialog brings
 * Escape, the focus trap and the scroll lock, and its field brings a focus
 * ring that a keyboard user can actually see.
 *
 * Every handler is unchanged.
 */
export default function CreateTeamModal({
  isOpen,
  onClose,
  onCreate,
  isCreating
}: CreateTeamModalProps) {
  const [organizationName, setOrganizationName] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onCreate(organizationName.trim() || undefined)
  }

  const handleClose = () => {
    if (!isCreating) {
      setOrganizationName('')
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <DsIsland panel={false} ground={false}>
      <Dialog
        open
        onClose={handleClose}
        title="Create New Team"
        lead="Share domains and collaborate with your team."
        actions={
          <>
            <Button variant="secondary" onClick={handleClose} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isCreating}>
              Create Team
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <Field
            label="Organization name"
            aside="Optional"
            hint="Give your team a recognizable name. You can change this later in settings."
          >
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Acme Inc."
                disabled={isCreating}
                maxLength={100}
                autoFocus
              />
            )}
          </Field>
        </form>
      </Dialog>
    </DsIsland>
  )
}
